import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import deviceService from '../services/deviceService';

// Create the context
const MemberContext = createContext(null);

export const MemberProvider = ({ children }) => {
  const [member, _setMember] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia('(orientation: portrait)').matches
  ); // State to track portrait/landscape orientation

  // State to track device orientation
  // It's possible that a customer may use our app upside down, so we want to consider the orientation
  // when the member enters their phone number as the "right side up".
  // This state value is the app's understanding of the device orientation.  When the app is "right side up",
  // the value is 1 and we show the member side of the application.  When the value is -1, we show the
  // cashier side of the application.  We keep the orientation as 0 until a customer enters their number and
  // then that "locks" the orientation of the device.
  const [orientation, setOrientation] = useState(0);  // 0 is unknown/logged out, 1 is right side up, -1 is upside down.

  // This will always have the devices current actual orientation.
  const deviceOrientation = useRef(0);

  // Reference to store the refresh interval
  const refreshIntervalRef = useRef(null);

  useEffect(() => {
    const handleDeviceOrientation = (event) => {
      let newOrientation = 0;
      if (window?.screen?.orientation?.type) {
        const [orientation, primary] = window.screen.orientation.type.split('-');
        newOrientation = primary === 'primary' ? -1 : 1;
      } else {
        // Check if we have valid orientation data
        if (event.beta === null || event.gamma === null) return;

        let facingUser = (isPortrait ? event.beta : event.gamma) > 0;
        newOrientation = facingUser ? -1 : 1;
      }

      // Only update if orientation actually changed
      if (newOrientation !== deviceOrientation.current) {
        // Always keep track of current orientation
        deviceOrientation.current = newOrientation;
        if (orientation !== 0) {
          // If we're in logged in mode and orientation changed then we need to flip the app. (e.g., from customer to cashier)
          setOrientation(original => original * -1);
        }
      }
    };

    // Check if DeviceOrientationEvent is supported
    if (window.DeviceOrientationEvent) {
      // Request permission on iOS 13+
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
          .then(response => {
            if (response === 'granted') {
              window.addEventListener('deviceorientation', handleDeviceOrientation);
            } else {
              console.warn('Device orientation permission denied');
            }
          })
          .catch(error => {
            console.error('Error requesting device orientation permission:', error);
            // Fallback: add listener anyway (for non-iOS devices)
            window.addEventListener('deviceorientation', handleDeviceOrientation);
          });
      } else {
        // Non-iOS devices or older iOS versions
        window.addEventListener('deviceorientation', handleDeviceOrientation);
      }
    } else {
      console.warn('DeviceOrientationEvent not supported');
    }

    // Cleanup on unmount
    return () => {
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
    };

    // TODO implement nginx for https for all of my apps...
  }, [orientation, isPortrait]);

  // Effect to track portrait/landscape orientation changes
  useEffect(() => {
    const handleOrientationChange = (e) => {
      setIsPortrait(e.matches);
    };

    // Create media query
    const mediaQuery = window.matchMedia('(orientation: portrait)');

    // Add listener
    mediaQuery.addEventListener('change', handleOrientationChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  // Auto-refresh member data when unverified or opted out
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Check if member needs auto-refresh
    if (member && (
      member.email_verified === false ||
      member.phone_verified === false ||
      member.is_optout === true
    )) {
      // Refresh member data every 10 seconds
      refreshIntervalRef.current = setInterval(async () => {
        try {
          const response = await deviceService.getMemberByPhone(member.phone);
          if (response.data) {
            _setMember(response.data);
          }
        } catch (error) {
          console.error('Error refreshing member data:', error);
        }
      }, 10000); // 10 seconds
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [member]);

  // Clear member information
  const clearMember = () => {
    _setMember(null);
    setSelectedOffer(null); // Clear selected offer when member is cleared
    setOrientation(0); // Reset orientation when member is cleared
  };

  // Update member (for future use)
  const updateMember = (updatedFields) => {
    _setMember(prev => prev ? { ...prev, ...updatedFields } : null);
  };

  const setMember = (member) => {
    _setMember(member);
    if (member) {
      setOrientation(1); // Set orientation to right side up when member is set
    } else {
      setOrientation(0); // Reset orientation when member is cleared
    }
  };

  return (
    <MemberContext.Provider
      value={{
        member,
        setMember,
        clearMember,
        updateMember,
        orientation,
        setOrientation,
        selectedOffer,
        setSelectedOffer,
        isPortrait,
      }}
    >
      {children}
    </MemberContext.Provider>
  );
};

// Custom hook to use the member context
export const useMember = () => {
  const context = useContext(MemberContext);
  if (!context) {
    throw new Error('useMember must be used within a MemberProvider');
  }
  return context;
};

export default MemberContext;